import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Check, Crown, Sparkles, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { applyImmediatePremiumUpdate } from '../utils/betaUpgradeRules';

function PricingPage() {
  const { user, isBartender, isBarOwner, setUser } = useAuth();
  const [activatingBeta, setActivatingBeta] = useState(false);

  const userTypeLabel = isBartender ? 'Bartender' : 'Bar Owner';

  const defaultPlanId = useMemo(() => {
    if (isBartender) return 'premium_bartender_monthly';
    if (isBarOwner) return 'bar_owner_professional';
    return null;
  }, [isBartender, isBarOwner]);

  const handleActivateFreeBeta = async () => {
    if (!defaultPlanId) {
      toast.error('Unable to determine beta plan for this account type.');
      return;
    }

    setActivatingBeta(true);
    try {
      const response = await axios.post('/beta/lock-in-plan', { plan_id: defaultPlanId });
      applyImmediatePremiumUpdate(response.data, setUser);
      toast.success(response.data?.message || 'Free Beta Status Active');
    } catch (error) {
      console.error('Failed to activate free beta:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate free beta');
    } finally {
      setActivatingBeta(false);
    }
  };
  
  // Define free features based on user type
  const bartenderFeatures = [
    'Create professional profile',
    'Browse and apply to unlimited jobs',
    'Direct messaging with bar owners',
    'AI-powered job matching',
    'Profile visibility boost',
    'Save favorite job listings',
    'Application tracking',
    'Review and rating system'
  ];
  
  const barOwnerFeatures = [
    'Post unlimited job listings',
    'Browse bartender profiles',
    'Direct messaging with bartenders',
    'AI-powered candidate matching',
    'Application management',
    'Review and rating system',
    'Priority job visibility',
    'Advanced search filters'
  ];
  
  const features = isBartender ? bartenderFeatures : barOwnerFeatures;

  if (user?.is_premium) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="pricing-page-premium-active">
        <Card className="bg-gradient-to-br from-primary/10 to-[#F5C15D]/10 border-primary/20">
          <CardHeader className="text-center">
            <CardTitle className="font-['Barlow_Condensed'] uppercase text-3xl text-white">Free Beta Upgrade Active</CardTitle>
            <CardDescription className="text-base">Your account is currently on premium beta access.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild data-testid="pricing-page-go-dashboard-btn">
              <Link to="/dashboard">Go to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center mb-12">
        {/* Free Beta Banner */}
        <div className="mb-6 inline-block">
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-lg px-6 py-2 animate-pulse">
            <Gift className="h-5 w-5 mr-2 inline" />
            🎉 FREE Beta Access - All Features Included! 🎉
          </Badge>
        </div>
        
        <div className="flex items-center justify-center mb-4">
          <Sparkles className="h-12 w-12 text-primary fill-current mr-3" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white font-['Barlow_Condensed']">
            {userTypeLabel} Access
          </h1>
        </div>
        <p className="text-xl text-muted-foreground mb-4">
          Join our beta program and get full access to all features — completely free!
        </p>
        <p className="text-lg text-green-400">
          No credit card required. No hidden fees. Just sign up and start connecting.
        </p>
      </div>

      {/* Single Pricing Card - Free Plan */}
      <div className="max-w-2xl mx-auto mb-12">
        <Card className="relative bg-white/5 backdrop-blur-md border border-primary/50 shadow-lg shadow-primary/20">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <Badge className="bg-primary text-white px-6 py-2">
              <Sparkles className="h-4 w-4 mr-2 fill-current" />
              Beta Access
            </Badge>
          </div>
          
          <CardHeader className="text-center pb-4 pt-8">
            <CardTitle className="font-['Barlow_Condensed'] uppercase text-3xl mb-4">
              Full Platform Access
            </CardTitle>
            <div className="mb-6">
              <div className="text-6xl font-bold text-primary mb-2">
                FREE
              </div>
              <p className="text-sm text-muted-foreground">
                During beta period
              </p>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <Button
              size="lg"
              className="w-full bg-primary text-white hover:bg-[hsl(var(--primary-light))] h-12 text-lg font-bold"
              onClick={handleActivateFreeBeta}
              disabled={activatingBeta}
              data-testid="activate-free-beta-pricing-btn"
            >
              {activatingBeta ? 'Activating...' : 'Activate Free Beta Upgrade'}
            </Button>

            {isBartender && (
              <Button asChild variant="outline" className="w-full" data-testid="view-bartender-premium-options-btn">
                <Link to="/premium">View Bartender Beta Plan Options</Link>
              </Button>
            )}
            
            <div className="space-y-3 mt-6">
              <p className="font-semibold text-white text-center mb-4">Everything included:</p>
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-6">
              <p className="text-sm text-primary text-center">
                💡 Early beta users will receive special perks when we launch premium tiers!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] uppercase text-center">
            Why Join Our Beta?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-semibold text-white mb-2">Help Shape the Platform</h3>
            <p className="text-sm text-muted-foreground">
              Your feedback during beta will help us build the best bar industry hiring platform. We&apos;re listening to our early users!
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-white mb-2">Full Feature Access</h3>
            <p className="text-sm text-muted-foreground">
              Get complete access to all features including AI matching, unlimited messaging, job postings, and more.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-white mb-2">Early Adopter Benefits</h3>
            <p className="text-sm text-muted-foreground">
              Beta users will receive exclusive benefits and discounts when we introduce premium features in the future.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PricingPage;
